import {
  Button,
  Alert,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle, IconPlus, IconSearch } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { AddendumMacroFormModal } from "../../../components/admin/addendum-macros/AddendumMacroFormModal";
import { AddendumMacroTableRow } from "../../../components/admin/addendum-macros/AddendumMacroTableRow";
import { ConfirmAddendumActionModal } from "../../../components/pathologist-review/ConfirmAddendumActionModal";
import {
  useAddendumMacrosAdmin,
  useCreateAddendumMacro,
  useDeleteAddendumMacro,
  useUpdateAddendumMacro,
} from "../../../hooks/admin/useAddendumMacros";
import type { AddendumMacro } from "../../../services/addendum.service";
import type {
  AddendumMacroInput,
  AddendumMacroType,
} from "../../../services/admin/addendumMacros.service";
import { stripHtml } from "../../../utils/helpers";

export default function AddendumMacrosAdminPage() {
  const [macroTypeFilter, setMacroTypeFilter] =
    useState<AddendumMacroType>("addendum");
  const {
    data: macros = [],
    isLoading,
    error,
  } = useAddendumMacrosAdmin(macroTypeFilter);
  const createMacro = useCreateAddendumMacro();
  const updateMacro = useUpdateAddendumMacro();
  const deleteMacro = useDeleteAddendumMacro();
  const [editorOpened, { open: openEditor, close: closeEditor }] =
    useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] =
    useDisclosure(false);
  const [editingMacro, setEditingMacro] = useState<AddendumMacro | null>(null);
  const [deletingMacro, setDeletingMacro] = useState<AddendumMacro | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const filteredMacros = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return macros;
    return macros.filter(
      (macro) =>
        macro.name.toLowerCase().includes(query) ||
        macro.shortcut?.toLowerCase().includes(query) ||
        stripHtml(macro.contentHtml).toLowerCase().includes(query)
    );
  }, [macros, search]);

  const handleAdd = useCallback(() => {
    setEditingMacro(null);
    setFormError(null);
    openEditor();
  }, [openEditor]);

  const handleEdit = useCallback(
    (macro: AddendumMacro) => {
      setEditingMacro(macro);
      setFormError(null);
      openEditor();
    },
    [openEditor]
  );

  const handleToggle = useCallback(
    async (macro: AddendumMacro) => {
      await updateMacro
        .mutateAsync({
          id: macro.id,
          input: { isActive: !macro.isActive },
        })
        .catch(() => undefined);
    },
    [updateMacro]
  );

  const handleDeleteRequest = useCallback(
    (macro: AddendumMacro) => {
      setDeletingMacro(macro);
      openDelete();
    },
    [openDelete]
  );

  const handleDelete = useCallback(async () => {
    if (!deletingMacro) return;
    const deleted = await deleteMacro
      .mutateAsync(deletingMacro.id)
      .then(() => true)
      .catch(() => false);
    if (!deleted) return;
    setDeletingMacro(null);
    closeDelete();
  }, [closeDelete, deleteMacro, deletingMacro]);

  const handleSubmit = useCallback(
    async (values: AddendumMacroInput) => {
      setFormError(null);
      try {
        if (editingMacro) {
          await updateMacro.mutateAsync({
            id: editingMacro.id,
            input: values,
          });
        } else {
          await createMacro.mutateAsync(values);
        }
        closeEditor();
      } catch (error) {
        setFormError("The macro could not be saved. Try again.");
      }
    },
    [closeEditor, createMacro, editingMacro, updateMacro]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.currentTarget.value);
    },
    []
  );
  const handleMacroTypeFilterChange = useCallback((value: string | null) => {
    if (value === "addendum" || value === "amendment") {
      setMacroTypeFilter(value);
    }
  }, []);

  return (
    <Container fluid py="xl">
      <Stack gap="lg" maw={1400} mx="auto">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Addendum and Amendment Macros</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Maintain reusable rich-text templates and fillable variables for
              signed-report changes.
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
            Add Macro
          </Button>
        </Group>
        <Paper withBorder p="md">
          <Group align="flex-end">
            <Select
              label="Macro type"
              value={macroTypeFilter}
              onChange={handleMacroTypeFilterChange}
              data={[
                { value: "addendum", label: "Addendum" },
                { value: "amendment", label: "Amendment" },
              ]}
              allowDeselect={false}
              w={220}
            />
            <TextInput
              value={search}
              onChange={handleSearchChange}
              leftSection={<IconSearch size={16} />}
              placeholder="Search name, shortcut, or content"
              style={{ flex: 1 }}
            />
          </Group>
        </Paper>
        <Paper withBorder p="md">
          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              mb="md"
              title="Report macros could not be loaded"
            >
              Refresh the page and try again.
            </Alert>
          )}
          {isLoading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : filteredMacros.length === 0 ? (
            <Text ta="center" c="dimmed" py="xl">
              No {macroTypeFilter} macros match this view.
            </Text>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Shortcut</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Content preview</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredMacros.map((macro) => (
                  <AddendumMacroTableRow
                    key={macro.id}
                    macro={macro}
                    onEdit={handleEdit}
                    onToggle={handleToggle}
                    onDelete={handleDeleteRequest}
                  />
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>
      <AddendumMacroFormModal
        key={`${editorOpened ? "open" : "closed"}-${editingMacro?.id ?? "new"}`}
        opened={editorOpened}
        macro={editingMacro}
        loading={createMacro.isPending || updateMacro.isPending}
        error={formError}
        onClose={closeEditor}
        onSubmit={handleSubmit}
      />
      <ConfirmAddendumActionModal
        opened={deleteOpened}
        title="Delete report macro?"
        message={`Delete “${
          deletingMacro?.name || "this macro"
        }”? Existing drafts and signed reports are unchanged.`}
        confirmLabel="Delete Macro"
        loading={deleteMacro.isPending}
        onCancel={closeDelete}
        onConfirm={handleDelete}
      />
    </Container>
  );
}
